import { API_URL } from 'constants/api';
import { USER } from 'constants/auth';
import dayjs from 'dayjs';
import SigninPage from 'pages/signin/signin';
import {
  FunctionComponent,
  PropsWithChildren,
  createContext,
  useState,
  useContext,
  useEffect,
} from 'react';
import { z } from 'zod';
import { wrappedFetch } from 'utils/api';

export type NotifyMethod = 'sms' | 'email';

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isReady: boolean;
  openLoginPage: () => void;
}

export const UserParser = z.object({
  id: z.number(),
  name: z.nullable(z.string()),
  city_id: z.nullable(z.number()),
  user_token: z.string(),
  email: z.nullable(z.string()),
  phone_number: z.nullable(z.string()),
  address: z.nullable(z.string()),
  email_notification: z.nullable(z.number()),
  sms_notification: z.nullable(z.number()),
  terms_agreed: z.nullable(z.boolean()),
});

export type User = z.infer<typeof UserParser>;

export const AuthContext = createContext<AuthContextType>({} as any);

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps extends PropsWithChildren {}

const AuthProvider: FunctionComponent<AuthProviderProps> = ({ children }) => {
  const [user, _setUser] = useState<User | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [openLogin, setOpenLogin] = useState(false);

  const setUser = (u: User | null) => {
    if (!u) {
      localStorage.removeItem(USER);
    } else {
      localStorage.setItem(USER, JSON.stringify(u));
    }
    _setUser(u);
  };

  useEffect(() => {
    const checkToken = async () => {
      const userString = localStorage.getItem(USER);
      if (userString) {
        const userData = JSON.parse(userString) as User;
        wrappedFetch(`${API_URL}token/check?user_id=${userData.id}`, {
          headers: { user_token: userData.user_token },
        })
          .then((res) => {
            if (res.data) {
              if (dayjs(res.data.expires * 1000).isAfter(new Date())) {
                _setUser(userData);
              }
            }
          })
          .catch(() => {});
      }
      setIsReady(true);
    };
    checkToken();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isReady,
        openLoginPage: () => setOpenLogin(true),
      }}
    >
      {children}
      <SigninPage open={openLogin} onClose={() => setOpenLogin(false)} />
    </AuthContext.Provider>
  );
};

export default AuthProvider;
